import React, { forwardRef, memo, useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Icon, Tab, Tooltip } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import * as PostEditorTabsTypes from "./PostEditorTabsTypes.constants";
import { getSocialProviderStyles } from "../../../_utils/socialProvider";

const tabStyles = ({ modified, error, isReadOnly }) => {
  const styles = {
    position: "relative",
    padding: "3",
  };

  if (error) {
    styles.color = "brand.negative";
  }

  if (isReadOnly) {
    styles._disabled = {
      cursor: "default",
    };
  }

  return styles;
};

const dotStyles = ({ modified, error }) => {
  const styles = {
    position: "absolute",
    top: "2",
    right: "1",
    display: "block",
    width: "2",
    height: "2",
    borderRadius: "full",
  };

  if (error) {
    styles.backgroundColor = "brand.negative";
  } else if (modified) {
    styles.backgroundColor = "gray.200";
  }

  return styles;
};

const iconWrapperStyles = {
  width: "1.2em",
  height: "1.2em",
  padding: "0.2em",
  lineHeight: 0,
  borderRadius: "full",
  color: "white",
};

const iconStyles = {
  fontSize: "0.8em", // 1.5 - (2 * 0.3)
};

const PostEditorTabIcon = forwardRef(({ icon, background, ...rest }, ref) => (
  <Box ref={ref} sx={iconWrapperStyles} backgroundColor={background} {...rest}>
    <Icon as={icon} sx={iconStyles} />
  </Box>
));

PostEditorTabIcon.propTypes = {
  icon: PropTypes.oneOfType([PropTypes.node, PropTypes.string, PropTypes.func])
    .isRequired,
  background: PropTypes.string,
};

PostEditorTabIcon.defaultProps = {
  background: undefined,
};

const PostEditorTab = forwardRef(
  ({ tab, modified, error, hideText, isReadOnly }, ref) => {
    const { t } = useTranslation(["planner"]);
    const title = useMemo(() => t(`post_editor.tabs.${tab}`), [tab, t]);
    const providerStyles = useMemo(() => {
      if (tab !== PostEditorTabsTypes.BASE) {
        return getSocialProviderStyles(tab);
      }

      return {};
    }, [tab]);

    return (
      <Tab
        ref={ref}
        sx={tabStyles({ modified, error, isReadOnly })}
        isDisabled={!!isReadOnly}
        fontWeight="500"
      >
        {providerStyles?.icon ? (
          <PostEditorTabIcon
            icon={providerStyles.icon}
            background={providerStyles?.background}
            marginRight={hideText ? null : "2"}
          />
        ) : null}
        {hideText && providerStyles?.icon ? null : <Box as="span">{title}</Box>}
        {modified || error ? (
          <Tooltip
            placement="top"
            hasArrow
            label={
              error
                ? t("post_editor.tab_state.error")
                : t("post_editor.tab_state.modified")
            }
          >
            <Box sx={dotStyles({ modified, error })} />
          </Tooltip>
        ) : null}
      </Tab>
    );
  }
);

PostEditorTab.propTypes = {
  tab: PropTypes.oneOf(Object.values(PostEditorTabsTypes)).isRequired,
  modified: PropTypes.bool,
  error: PropTypes.bool,
  hideText: PropTypes.bool,
  isReadOnly: PropTypes.bool,
};

PostEditorTab.defaultProps = {
  modified: undefined,
  error: undefined,
  hideText: undefined,
  isReadOnly: undefined,
};

export default memo(PostEditorTab);
